import { useState, useEffect } from 'react';
import api from '../api';
import { useLang } from '../context/LangContext';
import { useAuth } from '../context/AuthContext';

const ROLES = ['admin', 'viewer'];

function UserModal({ user, onClose, onSave, t }) {
  const isNew = !user?.id;
  const [form, setForm] = useState({
    name:     user?.name   || '',
    email:    user?.email  || '',
    role:     user?.role   || 'viewer',
    active:   user?.active ?? 1,
    password: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');

  function set(field, val) { setForm(f => ({ ...f, [field]: val })); }

  async function handleSubmit(e) {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      if (isNew) {
        await api.post('/api/users', form);
      } else {
        const payload = { name: form.name, email: form.email, role: form.role, active: form.active };
        if (form.password) payload.password = form.password;
        await api.put(`/api/users/${user.id}`, payload);
      }
      onSave();
    } catch (err) {
      setError(err.response?.data?.error || t('error'));
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      <div className="modal show d-block" tabIndex="-1">
        <div className="modal-dialog">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">{isNew ? t('user_new') : t('edit')}</h5>
              <button type="button" className="btn-close" onClick={onClose} />
            </div>
            <div className="modal-body">
              {error && <div className="alert alert-danger py-2">{error}</div>}
              <form id="user-form" onSubmit={handleSubmit}>
                <div className="mb-3">
                  <label className="form-label small fw-medium">{t('user_name')} *</label>
                  <input className="form-control" value={form.name}
                    onChange={e => set('name', e.target.value)} required autoFocus />
                </div>
                <div className="mb-3">
                  <label className="form-label small fw-medium">{t('user_email')} *</label>
                  <input type="email" className="form-control" value={form.email}
                    onChange={e => set('email', e.target.value)} required />
                </div>
                <div className="row g-3 mb-3">
                  <div className="col">
                    <label className="form-label small fw-medium">{t('user_role')}</label>
                    <select className="form-select" value={form.role} onChange={e => set('role', e.target.value)}>
                      {ROLES.map(r => <option key={r} value={r}>{t(`role_${r}`)}</option>)}
                    </select>
                  </div>
                  {!isNew && (
                    <div className="col-auto d-flex align-items-end pb-1">
                      <div className="form-check">
                        <input className="form-check-input" type="checkbox" id="active"
                          checked={!!form.active} onChange={e => set('active', e.target.checked ? 1 : 0)} />
                        <label className="form-check-label small" htmlFor="active">{t('user_active')}</label>
                      </div>
                    </div>
                  )}
                </div>
                <div className="mb-1">
                  <label className="form-label small fw-medium">
                    {t('user_password')}{!isNew && <span className="text-muted ms-1">({t('user_password_hint')})</span>}
                  </label>
                  <input type="password" className="form-control" value={form.password}
                    onChange={e => set('password', e.target.value)}
                    required={isNew} autoComplete="new-password" />
                </div>
              </form>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={onClose}>{t('cancel')}</button>
              <button type="submit" form="user-form" className="btn btn-primary" disabled={loading}>
                {loading && <span className="spinner-border spinner-border-sm me-2" />}
                {t('save')}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop show" />
    </>
  );
}

export default function UsersPage() {
  const { t } = useLang();
  const { user: me } = useAuth();
  const [users, setUsers]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal]     = useState(null);

  function load() {
    setLoading(true);
    api.get('/api/users').then(r => setUsers(r.data)).finally(() => setLoading(false));
  }

  useEffect(load, []);

  async function handleDelete(u) {
    if (!window.confirm(t('confirm_delete'))) return;
    await api.delete(`/api/users/${u.id}`);
    load();
  }

  return (
    <>
      <div className="page-header">
        <h2>{t('nav_users')}</h2>
        <button className="btn btn-primary btn-sm" onClick={() => setModal('new')}>
          <i className="bi bi-plus me-1" />{t('user_new')}
        </button>
      </div>

      <div className="page-body">
        <div className="card">
          {loading ? (
            <div className="card-body text-center text-muted py-4">
              <div className="spinner-border spinner-border-sm me-2" />{t('loading')}
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead className="table-light">
                  <tr>
                    <th>{t('user_name')}</th>
                    <th>{t('user_email')}</th>
                    <th>{t('user_role')}</th>
                    <th>{t('user_active')}</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {users.map(u => (
                    <tr key={u.id}>
                      <td className="fw-medium">
                        {u.name}
                        {u.id === me?.id && <span className="badge bg-secondary ms-2" style={{ fontSize: 10 }}>jij</span>}
                      </td>
                      <td className="text-muted">{u.email}</td>
                      <td><span className="badge bg-light text-dark border">{t(`role_${u.role}`)}</span></td>
                      <td>
                        {u.active
                          ? <span className="badge badge-verified">{t('user_active')}</span>
                          : <span className="badge badge-manual">{t('user_inactive')}</span>}
                      </td>
                      <td className="text-end">
                        <button className="btn btn-sm btn-outline-secondary me-2" onClick={() => setModal(u)}>
                          {t('edit')}
                        </button>
                        {u.id !== me?.id && (
                          <button className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(u)}>
                            <i className="bi bi-trash" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {modal && (
        <UserModal
          user={modal === 'new' ? null : modal}
          t={t}
          onClose={() => setModal(null)}
          onSave={() => { setModal(null); load(); }}
        />
      )}
    </>
  );
}
